import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { segments } from "@/data/segments";
import { fadeUp, EASE } from "@/lib/motion";

export default function BuyerSwitcher() {
  const [active, setActive] = useState(segments[0].id);
  const current = segments.find((s) => s.id === active);

  const move = (direction) => {
    const index = segments.findIndex((s) => s.id === active);
    setActive(segments[(index + direction + segments.length) % segments.length].id);
  };

  return (
    <section
      data-testid="buyer-switcher"
      className="relative bg-[#0a1930] text-[#f4f0e6] overflow-hidden grain"
    >
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-24 lg:py-40 relative z-10">
        <motion.div {...fadeUp} className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16">
          <div className="lg:col-span-4">
            <span className="overline-tag text-[#f26522]">Buyer entry — three sectors</span>
          </div>
          <div className="lg:col-span-8">
            <h2 className="font-display text-[clamp(2rem,5vw,4.75rem)] leading-[1.02] tracking-tight">
              Choose the relationship you already have,
              <br />
              <span className="font-display-italic text-[#f26522]">
                and add tangible value to it.
              </span>
            </h2>
          </div>
        </motion.div>

        <div
          role="tablist"
          aria-label="Buyer segments"
          className="grid grid-cols-[0.95fr_0.85fr_1.2fr] sm:grid-cols-3 border border-white/15 mb-10"
        >
          {segments.map((s, i) => {
            const isActive = active === s.id;
            return (
              <button
                key={s.id}
                id={`buyer-tab-${s.id}`}
                role="tab"
                aria-selected={isActive}
                aria-controls="buyer-panel"
                tabIndex={isActive ? 0 : -1}
                onClick={() => setActive(s.id)}
                onKeyDown={(e) => {
                  if (e.key === "ArrowRight") {
                    e.preventDefault();
                    move(1);
                  }
                  if (e.key === "ArrowLeft") {
                    e.preventDefault();
                    move(-1);
                  }
                }}
                data-testid={`buyer-tab-${s.id}`}
                className={`relative min-w-0 px-2 sm:px-4 lg:px-8 py-5 lg:py-6 font-mono text-[9px] sm:text-[11px] font-medium tracking-[0.14em] sm:tracking-[0.22em] uppercase leading-[1.45] text-center sm:text-left break-words transition-colors ${
                  isActive
                    ? "text-[#f4f0e6]"
                    : "text-[#f4f0e6]/50 hover:text-[#f4f0e6]"
                }`}
              >
                <span className="hidden sm:inline opacity-40 mr-3">
                  {String(i + 1).padStart(2, "0")}
                </span>
                {s.label}
                {isActive && (
                  <motion.span
                    layoutId="buyer-underline"
                    className="absolute inset-x-0 -bottom-px h-[2px] bg-[#f26522]"
                    transition={{ duration: 0.5, ease: EASE }}
                  />
                )}
              </button>
            );
          })}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            id="buyer-panel"
            role="tabpanel"
            aria-labelledby={`buyer-tab-${current.id}`}
            key={current.id}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.55, ease: EASE }}
            className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 pt-6"
          >
            <div className="lg:col-span-7">
              <p className="font-display text-[clamp(1.75rem,3.6vw,3.25rem)] leading-[1.1] tracking-tight max-w-[720px]">
                {current.lead}
              </p>
              <p className="mt-8 text-lg text-[#f4f0e6]/70 leading-relaxed max-w-[600px]">
                {current.body}
              </p>
              <Link
                to={current.href}
                data-testid={`buyer-cta-${current.id}`}
                className="group inline-flex items-center gap-3 mt-10 overline-tag text-[#f26522] hover:text-[#f4f0e6] transition-colors"
              >
                Read the {current.label.toLowerCase()} view
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </Link>
            </div>

            <div className="lg:col-span-5 lg:pl-10 lg:border-l border-white/15">
              <p className="overline-tag text-[#f4f0e6]/50 mb-6">The Medicual pathway</p>
              <ul className="space-y-4 text-sm text-[#f4f0e6]/80">
                <li className="flex items-start gap-3">
                  <span className="text-[#f26522] font-mono mt-1">+</span>
                  A licensed clinical pathway within the customer offer.
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#f26522] font-mono mt-1">+</span>
                  CE-marked. Operating continuously since 2013.
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#f26522] font-mono mt-1">+</span>
                  A digital entry point, doctor review and onward care when needed.
                </li>
              </ul>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      <div
        aria-hidden
        className="hidden md:block absolute -bottom-40 -left-40 w-[600px] h-[600px] rounded-full bg-[#f26522] opacity-[0.08] blur-[140px]"
      />
    </section>
  );
}
